/**
 * Icon library taxonomy — the vocabulary every icon's metadata is validated against.
 *
 * Three independent axes describe an icon:
 *   category  — what the icon LOOKS like / its visual family (used to group the gallery)
 *   areas     — where in the product it is USED (a booking icon can live in owner + caregiver + admin)
 *   style     — how it is DRAWN (line / duotone / animated)
 *
 * Keep the ids stable: they are persisted in `icon-metadata.json`, shipped in
 * `public/icons/icon-library.json`, and referenced by `<Icon name="..." />` consumers.
 */

export const LIBRARY_VERSION = '1.0.0';

/** Every custom icon is drawn on a 24×24 grid with artwork inset to 2px padding. */
export const ICON_GRID = 24;

/** Loaders use a wider stage so a running animal has room to move. */
export const LOADER_VIEWBOX = '0 0 64 48';

/**
 * Named sizes. Tailwind classes are provided because the app styles with the
 * Tailwind CDN; `px` is provided for the `size` prop and raw SVG exports.
 */
export const ICON_SIZES = {
  xs: { px: 14, className: 'w-3.5 h-3.5', usage: 'Inline with caption text, dense table cells' },
  sm: { px: 16, className: 'w-4 h-4', usage: 'Buttons, chips, badges, admin UI default' },
  md: { px: 20, className: 'w-5 h-5', usage: 'Body text, list rows, form field affordances' },
  lg: { px: 24, className: 'w-6 h-6', usage: 'Navigation, card headers, default app size' },
  xl: { px: 32, className: 'w-8 h-8', usage: 'Feature tiles, service cards, section headers' },
  '2xl': { px: 48, className: 'w-12 h-12', usage: 'Empty states, onboarding, hero accents' },
  '3xl': { px: 64, className: 'w-16 h-16', usage: 'Full-page empty states, splash, error pages' },
};

export const DEFAULT_ICON_SIZE = 'lg';

/** Optical stroke weights. Thinner strokes at large sizes keep icons from looking heavy. */
export const STROKE_WEIGHTS = {
  hairline: 1.25,
  light: 1.5,
  regular: 1.75,
  medium: 2,
  bold: 2.25,
};

export const DEFAULT_STROKE_WIDTH = STROKE_WEIGHTS.regular;

export const ICON_STYLES = {
  line: {
    id: 'line',
    label: 'Monoline',
    description: 'Single-weight outline, rounded caps and joins. The house style.',
  },
  duotone: {
    id: 'duotone',
    label: 'Duotone',
    description: 'Outline plus a soft accent fill drawn from --pf-accent-soft.',
  },
  animated: {
    id: 'animated',
    label: 'Animated',
    description: 'Motion built into the SVG. Honours prefers-reduced-motion.',
  },
};

export const ICON_SOURCES = {
  brand: {
    id: 'brand',
    label: 'Brand original',
    description: 'Hand-drawn for PetsFostering. Owned artwork, safe for marketing and app store use.',
  },
  animated: {
    id: 'animated',
    label: 'Brand animated',
    description: 'Hand-drawn animated loader. Brand original.',
  },
  lucide: {
    id: 'lucide',
    label: 'Lucide',
    description: 'Semantic alias over lucide-react (ISC licensed) so utility icons stay consistent.',
  },
};

/** Visual families — drives the gallery grouping order. */
export const CATEGORIES = [
  { id: 'brand', label: 'Brand & logo', description: 'Logo marks, favicons, the paw system.' },
  { id: 'pets', label: 'Pets & species', description: 'Animal faces, paws, pet identity.' },
  { id: 'services', label: 'Care services', description: 'One icon per service in the catalog.' },
  { id: 'booking', label: 'Booking & scheduling', description: 'Dates, slots, booking lifecycle.' },
  { id: 'communication', label: 'Communication', description: 'Chat, mail, calls, notifications.' },
  { id: 'trust', label: 'Trust & safety', description: 'Verification, badges, permissions, privacy.' },
  { id: 'money', label: 'Money', description: 'Payouts, pricing, invoices, expenses.' },
  { id: 'data', label: 'Data & analytics', description: 'Charts, reports, KPIs.' },
  { id: 'actions', label: 'Actions', description: 'Create, edit, delete, share, upload.' },
  { id: 'navigation', label: 'Navigation', description: 'Menus, chevrons, tabs, back/forward.' },
  { id: 'status', label: 'Status & feedback', description: 'Success, warning, error, info, empty.' },
  { id: 'media', label: 'Media & content', description: 'Photos, video, blog, attachments.' },
  { id: 'social', label: 'Social', description: 'Follow, share, external platforms.' },
  { id: 'settings', label: 'Settings & system', description: 'Preferences, theme, integrations.' },
  { id: 'loaders', label: 'Loaders & motion', description: 'Animated loading and progress states.' },
];

/** Product surfaces. Used to answer "what icons does the caregiver dashboard need?". */
export const AREAS = [
  { id: 'global', label: 'Global / shell', routes: ['*'] },
  { id: 'brand', label: 'Brand & marketing', routes: ['/', '/how-it-works', '/testimonials'] },
  { id: 'auth', label: 'Auth', routes: ['/login', '/signup'] },
  { id: 'onboarding', label: 'Onboarding', routes: ['/signup', '/owner-dashboard'] },
  { id: 'search', label: 'Find care / discovery', routes: ['/search', '/caregiver/:id'] },
  { id: 'pets', label: 'Pet profiles', routes: ['/owner-pets'] },
  { id: 'services', label: 'Service catalog', routes: ['/caregiver-services', '/search'] },
  { id: 'booking', label: 'Bookings', routes: ['/owner-bookings', '/caregiver-bookings', '/owner-booking/:id'] },
  { id: 'availability', label: 'Availability', routes: ['/caregiver-availability'] },
  { id: 'messaging', label: 'Messages', routes: ['/owner-messages', '/caregiver-messages'] },
  { id: 'reviews', label: 'Reviews & ratings', routes: ['/owner-reviews', '/caregiver-reviews'] },
  { id: 'profile', label: 'User & caregiver profiles', routes: ['/profile/:id', '/edit-caregiver-profile'] },
  { id: 'payments', label: 'Payments & earnings', routes: ['/caregiver-expenses', '/owner-bookings'] },
  { id: 'offlineJobs', label: 'Offline jobs', routes: ['/owner-offline-jobs', '/caregiver-offline-jobs'] },
  { id: 'trust', label: 'Badges & trust', routes: ['/admin-badges', '/caregiver/:id'] },
  { id: 'content', label: 'Blog & content', routes: ['/blog', '/admin-marketing'] },
  { id: 'admin', label: 'Admin dashboard', routes: ['/admin-dashboard', '/admin-settings'] },
  { id: 'analytics', label: 'Reporting & analytics', routes: ['/admin-reporting'] },
  { id: 'settings', label: 'Settings & theming', routes: ['/owner-settings', '/caregiver-settings', '/admin-settings'] },
  { id: 'loading', label: 'Loading & empty states', routes: ['*'] },
  { id: 'errors', label: 'Errors & recovery', routes: ['*'] },
];

export const CATEGORY_IDS = CATEGORIES.map((c) => c.id);
export const AREA_IDS = AREAS.map((a) => a.id);

export const getCategory = (id) => CATEGORIES.find((c) => c.id === id) || null;
export const getArea = (id) => AREAS.find((a) => a.id === id) || null;

/**
 * Accessibility contract for every icon.
 * Decorative icons must be hidden; meaningful icons must carry a label.
 */
export const A11Y_RULES = {
  decorative: 'Icon sits next to a text label. Renders aria-hidden="true" and no <title>.',
  meaningful: 'Icon is the only content (icon-only button). Pass `title` — renders role="img" + <title>.',
  status: 'Icon conveys state (success/error). Pair with text; never rely on colour alone.',
};

export default {
  LIBRARY_VERSION,
  ICON_GRID,
  ICON_SIZES,
  STROKE_WEIGHTS,
  CATEGORIES,
  AREAS,
  ICON_STYLES,
  ICON_SOURCES,
};
